import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import { useStrategyPerformance } from './useStrategyPerformance';

const RANGE_OPTIONS = [1, 2, 3, 4, 5, 7, 10, 14, 21, 30, 60, 90, 180, 365];

const formatRupees = (value) => {
    if (value === null || value === undefined) return '—';
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)}Cr`;
    if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)}L`;
    return `${sign}₹${abs.toFixed(0)}`;
};

const formatPnl = (value) => {
    if (value === null || value === undefined) return '—';
    return value >= 0 ? `+${formatRupees(value)}` : formatRupees(value);
};

const pnlColor = (value, theme) => {
    if (value === null || value === undefined || value === 0) return 'text.secondary';
    return value > 0 ? 'success.main' : 'error.main';
};

const formatPct = (value, digits = 1) => {
    if (value === null || value === undefined) return '—';
    return `${(value * 100).toFixed(digits)}%`;
};

const formatNumber = (value, digits = 0) => {
    if (value === null || value === undefined) return '—';
    return Number(value).toLocaleString('en-IN', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
};

const SectionHeading = ({ title, subtitle }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
        </Typography>
        {subtitle ? (
            <Typography variant="body2" color="text.secondary">
                {subtitle}
            </Typography>
        ) : null}
    </Box>
);

const SectionPaper = ({ children, sx = {} }) => (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, ...sx }}>
        {children}
    </Paper>
);

const RegimeChip = ({ regime }) => {
    if (!regime) return <Chip label="—" size="small" variant="outlined" />;
    const colors = {
        TRENDING: 'success',
        RANGING: 'info',
        VOLATILE: 'warning',
        WARMING_UP: 'default',
        UNKNOWN: 'default',
    };
    return <Chip label={regime} size="small" color={colors[regime] || 'default'} variant="outlined" />;
};

const DailyTable = ({ daily, strategies }) => {
    if (!daily || !daily.length) {
        return <Typography color="text.secondary">No trading days in range.</Typography>;
    }
    const ordered = [...daily].sort((a, b) => (a.date < b.date ? 1 : -1));
    return (
        <TableContainer>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Regime</TableCell>
                        {strategies.map((s) => (
                            <React.Fragment key={s.id}>
                                <TableCell align="right">{s.label} P&L</TableCell>
                                <TableCell align="right">{s.label} RTs</TableCell>
                            </React.Fragment>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ordered.map((row) => (
                        <TableRow key={row.date} hover>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>
                                <RegimeChip regime={row.regime} />
                            </TableCell>
                            {strategies.map((s) => {
                                const cell = row.byStrategy?.[s.id] || {};
                                return (
                                    <React.Fragment key={s.id}>
                                        <TableCell align="right" sx={{ color: pnlColor(cell.netPnl) }}>
                                            {formatPnl(cell.netPnl)}
                                        </TableCell>
                                        <TableCell align="right">{cell.rts || 0}</TableCell>
                                    </React.Fragment>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const SummaryCards = ({ summary, strategies }) => (
    <Grid container spacing={2}>
        {strategies.map((s) => {
            const stat = summary?.[s.id] || {};
            return (
                <Grid item xs={12} md={3} key={s.id}>
                    <SectionPaper sx={{ height: '100%' }}>
                        <Typography variant="overline" color="text.secondary">
                            {s.label}
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Total net P&L
                                </Typography>
                                <Typography variant="h5" sx={{ color: pnlColor(stat.totalNetPnl), fontWeight: 700 }}>
                                    {formatPnl(stat.totalNetPnl)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                <Stat label="Win rate" value={formatPct(stat.winRate)} />
                                <Stat label="Profit factor" value={formatNumber(stat.profitFactor, 2)} />
                                <Stat label="Avg winner" value={formatPnl(stat.avgWinner)} positive />
                                <Stat label="Avg loser" value={formatPnl(stat.avgLoser)} negative />
                                <Stat label="Max DD" value={formatPnl(stat.maxDailyDrawdown)} negative />
                                <Stat label="Sharpe (252)" value={formatNumber(stat.sharpe, 2)} />
                                <Stat label="Round trips" value={formatNumber(stat.totalRTs)} />
                                <Stat label="Active days" value={formatNumber(stat.tradingDaysWithActivity)} />
                            </Box>
                        </Stack>
                    </SectionPaper>
                </Grid>
            );
        })}
    </Grid>
);

const Stat = ({ label, value, positive, negative }) => {
    let color = 'text.primary';
    if (positive) color = 'success.main';
    else if (negative) color = 'error.main';
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
                {value}
            </Typography>
        </Box>
    );
};

const RegimeBreakdown = ({ regimeBreakdown, strategies }) => {
    const regimes = Object.keys(regimeBreakdown || {});
    if (!regimes.length) {
        return (
            <Typography color="text.secondary">
                Regime classification needs at least one V3 entry event in the window — none in range yet.
            </Typography>
        );
    }
    const order = ['TRENDING', 'RANGING', 'VOLATILE', 'WARMING_UP', 'UNKNOWN'];
    const ordered = regimes.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Regime</TableCell>
                        {strategies.map((s) => (
                            <React.Fragment key={s.id}>
                                <TableCell align="right">{s.label} avg/day</TableCell>
                                <TableCell align="right">{s.label} win%</TableCell>
                                <TableCell align="right">{s.label} days</TableCell>
                            </React.Fragment>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ordered.map((regime) => (
                        <TableRow key={regime} hover>
                            <TableCell>
                                <RegimeChip regime={regime} />
                            </TableCell>
                            {strategies.map((s) => {
                                const cell = regimeBreakdown[regime]?.[s.id] || {};
                                return (
                                    <React.Fragment key={s.id}>
                                        <TableCell align="right" sx={{ color: pnlColor(cell.avgDailyPnl) }}>
                                            {formatPnl(cell.avgDailyPnl)}
                                        </TableCell>
                                        <TableCell align="right">{formatPct(cell.winRate)}</TableCell>
                                        <TableCell align="right">{cell.days || 0}</TableCell>
                                    </React.Fragment>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const MarginAnalysis = ({ margin, strategies }) => {
    const byStrategy = margin?.byStrategy || {};
    return (
        <Grid container spacing={2}>
            {strategies.map((s) => {
                const m = byStrategy[s.id] || {};
                // V4 buys long options — no broker margin requirement.
                // The store substitutes premium-outlay aggregates so the
                // card is a like-for-like "capital deployment" view.
                const isOutlayBased = m.source === 'premium_outlay';
                const sectionLabel = isOutlayBased ? 'Capital deployed (premium outlay)' : 'Margin required';
                return (
                    <Grid item xs={12} md={3} key={s.id}>
                        <SectionPaper sx={{ height: '100%' }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography variant="overline" color="text.secondary">
                                    {s.label}
                                </Typography>
                                {isOutlayBased ? (
                                    <Chip label="long-only" size="small" variant="outlined" color="info" />
                                ) : null}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                {sectionLabel}
                            </Typography>
                            <Stack spacing={1}>
                                <Stat
                                    label={isOutlayBased ? 'Entries' : 'Snapshots'}
                                    value={formatNumber(m.snapshots)}
                                />
                                <Stat
                                    label={isOutlayBased ? 'Avg outlay / trade' : 'Avg margin required'}
                                    value={formatRupees(m.avgRequired)}
                                />
                                <Stat
                                    label={isOutlayBased ? 'Peak outlay / trade' : 'Max margin required'}
                                    value={formatRupees(m.maxRequired)}
                                />
                                <Stat
                                    label={isOutlayBased ? 'Avg outlay / equity' : 'Avg utilisation'}
                                    value={m.avgUtilisationPct != null ? `${m.avgUtilisationPct}%` : '—'}
                                />
                                {isOutlayBased ? null : (
                                    <Stat label="Insufficient blocks" value={formatNumber(m.insufficientCount)} />
                                )}
                                <Stat
                                    label={isOutlayBased ? 'P&L per ₹ deployed' : 'P&L per ₹ margin'}
                                    value={m.pnlPerRsMargin != null ? m.pnlPerRsMargin.toFixed(4) : '—'}
                                />
                            </Stack>
                        </SectionPaper>
                    </Grid>
                );
            })}
        </Grid>
    );
};

const TcaAnalysis = ({ tca, strategies }) => {
    if (!tca) return null;
    return (
        <>
            <Grid container spacing={2}>
                {strategies.map((s) => {
                    const t = tca[s.id] || {};
                    return (
                        <Grid item xs={12} md={3} key={s.id}>
                            <SectionPaper sx={{ height: '100%' }}>
                                <Typography variant="overline" color="text.secondary">
                                    {s.label}
                                </Typography>
                                <Stack spacing={1} sx={{ mt: 1 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Gross P&L
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: pnlColor(t.totalGrossPnl), fontWeight: 700 }}>
                                            {formatPnl(t.totalGrossPnl)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Total TCA
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
                                            {t.totalTca != null ? `-${formatRupees(t.totalTca)}` : '—'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Net P&L (after TCA)
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: pnlColor(t.totalNetPnl), fontWeight: 700 }}>
                                            {formatPnl(t.totalNetPnl)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                        <Stat label="TCA / RT" value={formatRupees(t.tcaPerRt)} />
                                        <Stat label="TCA % of gross" value={t.tcaPctOfGross != null ? `${t.tcaPctOfGross}%` : '—'} />
                                        <Stat label="Avg daily TCA" value={formatRupees(t.avgDailyTca)} />
                                        <Stat label="Max daily TCA" value={formatRupees(t.maxDailyTca)} />
                                        <Stat label="Round trips" value={formatNumber(t.totalRTs)} />
                                    </Box>
                                </Stack>
                            </SectionPaper>
                        </Grid>
                    );
                })}
            </Grid>
        </>
    );
};

const TcaDailyTableFromDaily = ({ daily, strategies }) => {
    if (!daily || !daily.length) {
        return <Typography color="text.secondary">No trading days in range.</Typography>;
    }
    const ordered = [...daily]
        .filter((row) => strategies.some((s) => {
            const cell = row.byStrategy?.[s.id];
            return cell && (cell.tca != null || cell.grossPnl != null);
        }))
        .sort((a, b) => (a.date < b.date ? 1 : -1));
    if (!ordered.length) {
        return <Typography color="text.secondary">No TCA data available in the selected range.</Typography>;
    }
    return (
        <TableContainer>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        {strategies.map((s) => (
                            <React.Fragment key={s.id}>
                                <TableCell align="right">{s.label} Gross</TableCell>
                                <TableCell align="right">{s.label} TCA</TableCell>
                                <TableCell align="right">{s.label} Net</TableCell>
                            </React.Fragment>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ordered.map((row) => (
                        <TableRow key={row.date} hover>
                            <TableCell>{row.date}</TableCell>
                            {strategies.map((s) => {
                                const cell = row.byStrategy?.[s.id] || {};
                                return (
                                    <React.Fragment key={s.id}>
                                        <TableCell align="right" sx={{ color: pnlColor(cell.grossPnl) }}>
                                            {formatPnl(cell.grossPnl)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: cell.tca ? 'error.main' : 'text.secondary' }}>
                                            {cell.tca != null ? `-${formatRupees(cell.tca)}` : '—'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: pnlColor(cell.netPnl) }}>
                                            {formatPnl(cell.netPnl)}
                                        </TableCell>
                                    </React.Fragment>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const StrategyPerformancePage = () => {
    const [rangeDays, setRangeDays] = useState(30);
    const { data, error, loading, refresh } = useStrategyPerformance({ rangeDays });

    const strategies = data?.strategies || [];
    const headerSummary = useMemo(() => {
        if (!data) return null;
        return strategies.map((s) => {
            const stat = data.summary?.[s.id] || {};
            return { id: s.id, label: s.label, totalNetPnl: stat.totalNetPnl, totalRTs: stat.totalRTs };
        });
    }, [data, strategies]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Strategy Performance
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Side-by-side comparison of qCobra, V2, V3, V4 &amp; Chanakya Inverse — daily P&amp;L, TCA,
                        regime breakdown, and margin efficiency from {data?.fromDate || '—'} to {data?.toDate || '—'}.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="strategy-perf-range">Range</InputLabel>
                        <Select
                            labelId="strategy-perf-range"
                            label="Range"
                            value={rangeDays}
                            onChange={(e) => setRangeDays(Number(e.target.value))}
                        >
                            {RANGE_OPTIONS.map((days) => (
                                <MenuItem key={days} value={days}>
                                    Last {days} {days === 1 ? 'day' : 'days'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <IconButton onClick={refresh} size="small" aria-label="refresh">
                        <RefreshIcon />
                    </IconButton>
                </Stack>
            </Stack>

            {headerSummary ? (
                <Grid container spacing={2}>
                    {headerSummary.map((s) => (
                        <Grid item xs={12} md={3} key={s.id}>
                            <SectionPaper>
                                <Typography variant="overline" color="text.secondary">
                                    {s.label}
                                </Typography>
                                <Typography variant="h4" sx={{ color: pnlColor(s.totalNetPnl), fontWeight: 700 }}>
                                    {formatPnl(s.totalNetPnl)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {formatNumber(s.totalRTs)} round trips in window
                                </Typography>
                            </SectionPaper>
                        </Grid>
                    ))}
                </Grid>
            ) : null}

            {loading && !data ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : null}

            {error && !data ? (
                <Alert severity="error">Failed to load strategy performance: {String(error.message || error)}</Alert>
            ) : null}

            {data ? (
                <>
                    <SectionPaper>
                        <SectionHeading
                            title="Daily P&L"
                            subtitle="Each row is a trading day. Click into a row in the future for per-RT detail."
                        />
                        <DailyTable daily={data.daily} strategies={strategies} />
                    </SectionPaper>

                    <SectionHeading title="Summary metrics" subtitle="Window totals and risk-adjusted performance." />
                    <SummaryCards summary={data.summary} strategies={strategies} />

                    <SectionHeading
                        title="Transaction Cost Analysis"
                        subtitle="Gross → TCA → Net breakdown per strategy. TCA includes brokerage, STT, exchange charges, GST, stamp duty, and SEBI fees."
                    />
                    <TcaAnalysis tca={data.tca} strategies={strategies} />
                    <SectionPaper sx={{ mt: 2 }}>
                        <SectionHeading
                            title="Daily TCA detail"
                            subtitle="Per-day gross P&L, TCA cost, and net P&L across strategies."
                        />
                        <TcaDailyTableFromDaily daily={data.daily} strategies={strategies} />
                    </SectionPaper>

                    <SectionPaper>
                        <SectionHeading
                            title="Regime breakdown"
                            subtitle="Per-day regime is derived from V3 entry events. Days without V3 activity show as UNKNOWN."
                        />
                        <RegimeBreakdown regimeBreakdown={data.regimeBreakdown} strategies={strategies} />
                    </SectionPaper>

                    <SectionHeading
                        title="Margin / Capital analysis"
                        subtitle="V1, V2, V3 sell strangles → broker margin required, captured pre-entry into margin_snapshots. V4 buys long options → no margin (only premium outlay), so its card shows premium-outlay aggregates from the journal as a like-for-like capital-deployment view. ‘P&L per ₹’ is the strategy's return on the capital it actually used."
                    />
                    <MarginAnalysis margin={data.margin} strategies={strategies} />
                </>
            ) : null}
        </Box>
    );
};

export default StrategyPerformancePage;
